// ─── Public Status Page — component prop contracts ───────────────────
//
// THE interface boundary between src/status/App.tsx (which owns the page
// layout, the route, the data hook and all section chrome) and the
// presentational components under src/status/components/.
//
// Every component listed here is CONTENT ONLY: App.tsx renders the
// <section class="sp-section"> wrapper and its <h2>, so a component never
// invents its own heading, never reads the route and never fetches. That keeps
// the page's information architecture in one file and makes each component
// trivially previewable with a literal payload.
//
// Danish copy comes from ./i18n/da.ts, colours from ./lib/levels.ts, dates from
// ./lib/format.ts — never a hardcoded string, hex or locale.

import type { ReactNode } from 'react';
import type {
  PublicStatusComponent,
  PublicStatusIncident,
  PublicStatusSummary,
  StatusComponentStatus,
  StatusFilterLabel,
} from '@pcp/shared';
import type { StatusSource } from './lib/statusApi';

/** The id App.tsx points its skip link at; StatusShell MUST put it on <main>. */
export const STATUS_MAIN_ID = 'sp-main';

// ─── Shell ───────────────────────────────────────────────────────────

/**
 * The page frame: a sticky header, the scrolling <main id={STATUS_MAIN_ID}>
 * (which is also the skip-link target, so it needs `tabIndex={-1}`), and the
 * footer. It renders no content of its own.
 */
export interface StatusShellProps {
  header: ReactNode;
  footer: ReactNode;
  children: ReactNode;
}

// ─── Header ──────────────────────────────────────────────────────────

/**
 * Brand + the single primary action (Tilmeld). There is deliberately NO audience
 * switcher — see the note at the top of StatusHeader.tsx: an audience page is
 * reached by the link its customers were given, not by browsing to it.
 */
export interface StatusHeaderProps {
  /** Name of the audience being rendered, shown under the logo. `null` on the
   *  combined view, which needs no qualifier. */
  audienceName: string | null;
  /** Public subscribe is configured AND at least one channel is enabled. */
  subscribeEnabled: boolean;
  /** Opens the SubscribeSheet. Only called when `subscribeEnabled`. */
  onSubscribe: () => void;
  /** Brand/logo click → the overview for the active audience. */
  onHome: () => void;
  /** href for the brand link, so middle-click / Ctrl-click opens a real tab. */
  homeHref: string;
}

// ─── Banner ──────────────────────────────────────────────────────────

/**
 * The one-line verdict at the top of the page: "Alle systemer kører normalt"
 * or the worst-wins headline, plus the open incidents behind it.
 */
export interface StatusBannerProps {
  overall: StatusComponentStatus;
  /** Server-rendered Danish label; fall back to levels.ts only if it is empty. */
  overallLabel: string;
  /** ISO — when the API generated this payload (`summary.generatedAt`). */
  generatedAt: string;
  /** Unresolved incidents (state === 'open'), newest first. May be empty. */
  activeIncidents: PublicStatusIncident[];
  /** Opens an incident's detail route. */
  onOpenIncident: (incident: PublicStatusIncident) => void;
  /** href builder for those links (plain left click is intercepted, the rest fall through). */
  hrefForIncident: (incident: PublicStatusIncident) => string;
}

// ─── Degraded notice ─────────────────────────────────────────────────

/**
 * Shown ONLY when the live API could not be reached and the page is rendering
 * the static snapshot — the whole reason this page exists. It must never look
 * like an outage banner: it explains that the figures below may be stale.
 */
export interface DegradedNoticeProps {
  /** ISO `generatedAt` of the snapshot on screen. */
  generatedAt: string;
  /** epoch ms of the last successful fetch (live or snapshot). */
  fetchedAt: number | null;
  /** Consecutive TOTAL failures — >0 means even the snapshot is unreachable now. */
  failures: number;
  onRetry: () => void;
}

// ─── History grid ────────────────────────────────────────────────────

/**
 * The day-per-cell uptime bar. Deliberately generic: App.tsx renders one at
 * page scale (`size="lg"`, `showLegend`) inside the "Overblik" section, and
 * ComponentTree renders one per component (`size="sm"`).
 *
 * Use `useElementWidth` (./lib/useElementWidth) to decide how many of the
 * newest days actually fit — 90 cells do not fit a phone. Always drop from the
 * OLDEST end so "today" stays flush right.
 */
export interface HistoryGridProps {
  /** One STATUS_HISTORY_CHARS char per day, OLDEST → NEWEST, length === historyDays. */
  history: string;
  /** Per-day information mask, indexed in step with `history` ('1' = a notice
   *  applied that day). Omit it — as the page-wide bar does — and no markers
   *  are drawn. */
  info?: string;
  /**
   * `(date, componentId)` → the href for that day's page. Omit and the cells stay
   * inert.
   *
   * It takes the componentId rather than being BOUND to one at each call site on
   * purpose: a bound closure is a new function identity on every render, which
   * would re-render 28 rows × 90 cells on every poll and defeat the memo that
   * HistoryGridRow exists for. The grid already knows its own componentId, so it
   * supplies it.
   */
  hrefForDay?: (date: string, componentId: string | null) => string;
  /** Keyboard activation on the strip (Enter/Space opens the focused day). */
  onOpenDay?: (date: string, componentId: string | null) => void;
  historyDays: number;
  /** YYYY-MM-DD of history[0] (`summary.historyStartDate`). */
  historyStartDate: string;
  /**
   * Minutes lost per day, indexed in step with `history`. The GRAPHIC ignores it
   * entirely — a day is still one slot, painted by the day's worst status — but
   * the uptime figure underneath is computed from it, so a five-minute blip and
   * a nine-hour outage no longer cost the same.
   *
   * Optional: an older snapshot has no downtime series, and the figure falls
   * back to counting whole days rather than disappearing.
   */
  downtime?: readonly number[];
  /** Minutes in each rendered day (`summary.dayMinutes`) — the denominator. */
  dayMinutes?: readonly number[];
  /** Accessible name for the bar — the component name, or the page-wide label. */
  label: string;
  /** Renders the "Ældre … I dag" legend row and the uptime figure beneath the bar. */
  showLegend?: boolean;
  /** 'lg' = the page overview bar, 'sm' = inside a component row. Default 'sm'. */
  size?: 'sm' | 'lg';
}

// ─── Components list ─────────────────────────────────────────────────

/**
 * The "Systemer" body: the ROOT components, each carrying its own subtree.
 * Renders no <h2> — App.tsx owns the section heading.
 */
export interface ComponentTreeProps {
  /** Day navigation, threaded to the strip. Stable callbacks — see HistoryGridProps. */
  hrefForDay?: (date: string, componentId: string | null) => string;
  onOpenDay?: (date: string, componentId: string | null) => void;
  components: PublicStatusComponent[];
  historyDays: number;
  historyStartDate: string;
  /** `summary.dayMinutes`; forwarded to each row's strip. */
  dayMinutes?: readonly number[];
  /** A row's name links into /component/<id>. */
  onOpenComponent: (component: { id: string }) => void;
  hrefForComponent: (component: { id: string }) => string;
}

// ─── Incident filters ────────────────────────────────────────────────

export interface IncidentFilterOption {
  value: StatusFilterLabel;
  /** Danish chip label (da.filterLabels[value]). */
  label: string;
  /** How many incidents in the unfiltered list carry this chip. */
  count: number;
}

/**
 * The seven filter chips + the free-text search. Multi-select, OR'd together;
 * an empty selection means "everything".
 */
export interface IncidentFiltersProps {
  /** In canonical STATUS_FILTER_LABELS order; options with count 0 are omitted by App. */
  options: IncidentFilterOption[];
  selected: StatusFilterLabel[];
  onToggle: (label: StatusFilterLabel) => void;
  /** Clears both the chips and the query. Hidden when nothing is active. */
  onClear: () => void;
  query: string;
  onQueryChange: (query: string) => void;
}

// ─── Incident list ───────────────────────────────────────────────────

export interface IncidentListProps {
  /** Already filtered + sorted by App.tsx (pinned first, then newest occurredAt). */
  incidents: PublicStatusIncident[];
  /** True when a chip/query is active — drives the "no match" copy vs "no incidents". */
  filtered: boolean;
  onOpenIncident: (incident: PublicStatusIncident) => void;
  hrefForIncident: (incident: PublicStatusIncident) => string;
}

// ─── Incident detail ─────────────────────────────────────────────────

/**
 * The /incident/:ref route. `incident` is null while `state !== 'ready'`.
 * A cold deep link (an incident older than the summary window) is fetched by
 * App.tsx, so this component still never fetches.
 */
export interface IncidentDetailProps {
  incident: PublicStatusIncident | null;
  state: 'loading' | 'ready' | 'notfound';
  onBack: () => void;
  /** href of the overview for the active audience. */
  backHref: string;
}

// ─── Component detail ────────────────────────────────────────────────

/**
 * The /component/:id route: one system in full — what it is, how it has been
 * doing over the rendered window, and every incident that touched it.
 *
 * Unlike the incident detail there is NO cold-fetch path and no `notfound`
 * network state to wait on: everything here is already in the summary the page
 * polls, so App.tsx resolves the id against `data.components` and this renders.
 * That is deliberate — it means a component deep link keeps working off the
 * static snapshot when the API is down, which is exactly when someone is most
 * likely to be following one out of an incident email.
 */
export interface ComponentDetailProps {
  /** Day navigation, threaded to the strip. Stable callbacks — see HistoryGridProps. */
  hrefForDay?: (date: string, componentId: string | null) => string;
  onOpenDay?: (date: string, componentId: string | null) => void;
  /** The resolved node, carrying its own rolled-up status/history/downtime. */
  component: PublicStatusComponent | null;
  /** Ancestors, ROOT FIRST, excluding the component itself. Empty for a root. */
  ancestors: Array<{ id: string; name: string }>;
  /** Incidents that touched this component or anything beneath it, newest first. */
  incidents: PublicStatusIncident[];
  /** 'loading' until the summary lands; 'notfound' once it has and the id is not in it. */
  state: 'loading' | 'ready' | 'notfound';
  historyDays: number;
  historyStartDate: string;
  /** `summary.dayMinutes` — the uptime denominator. */
  dayMinutes: readonly number[];
  onBack: () => void;
  /** href of the overview for the active audience. */
  backHref: string;
  onOpenIncident: (incident: PublicStatusIncident) => void;
  hrefForIncident: (incident: PublicStatusIncident) => string;
  /** Navigation into a child/ancestor component's own page. */
  onOpenComponent: (component: { id: string }) => void;
  hrefForComponent: (component: { id: string }) => string;
}

// ─── Subscription pages (/manage, /unsubscribe) ──────────────────────

/**
 * A subscriber's own page, reached from a link in an email or an SMS.
 *
 * Two modes rather than two components, because they are two states of one
 * thing: 'unsubscribe' performs the stop on mount and then offers the undo,
 * 'manage' shows what the subscription covers and lets it be changed.
 *
 * Deliberately NOT audience-scoped — the link comes out of an email where the
 * subscription is the subject, not a slice of the page.
 */
export interface SubscriptionPageProps {
  mode: 'manage' | 'unsubscribe';
  /** The short code from the link. Either this or `key` is present. */
  code: string | null;
  /** The full manage key, which is what the confirm route redirects with.
   *  NOT named `key` — React reserves that prop name. */
  manageKey: string | null;
  /** The redirect carried `?error=` — the link did not resolve server-side. */
  linkError: boolean;
  onBack: () => void;
  backHref: string;
}

// ─── Subscribe sheet ─────────────────────────────────────────────────

/**
 * The only stateful component on the page. It owns the whole flow itself —
 * postSubscribeSession → Turnstile (when a site key comes back) → postSubscribe
 * → the SMS OTP step via postSubscribeVerify (all in ./lib/statusApi) — because
 * none of that state is of any interest to the rest of the page.
 *
 * Mount it only when `open` can become true; render nothing when `open` is
 * false. Close on Escape and on backdrop click, and restore focus to the
 * trigger.
 */
export interface SubscribeSheetProps {
  open: boolean;
  onClose: () => void;
  /** Pre-ticks the audience matching the current route; `null` pre-ticks nothing. */
  initialAudienceSlug: string | null;
  // NO channel flags. Both fields render unconditionally: whether a channel is
  // open is OUR operational state, and the form used to leak it as "denne kanal
  // er ikke aktiv endnu". A visitor leaves their address and, if they want,
  // their number; when the channel opens is an admin's business, not theirs.
}

// ─── Footer ──────────────────────────────────────────────────────────

export interface StatusFooterProps {
  /** ISO `generatedAt` of the payload on screen, or null before the first load. */
  generatedAt: string | null;
  /** epoch ms of the last successful fetch. */
  fetchedAt: number | null;
  /** 'snapshot' ⇒ the footer says so too, not just the DegradedNotice. */
  source: StatusSource;
  onRefresh: () => void;
  /** True while a manual refresh is in flight — spin the icon, disable the button. */
  refreshing: boolean;
}
